<script setup lang="ts">
import { computed, h, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import { CRUISE, LANG_ENGLISH, ONEWAY, OPENJAW, ROUNDTRIP, STATUS_OPEN } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useModalComposable } from '@/views/composable';
import { useTabComposable } from '../useTabComposable';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useAirPortionStore } from '@/store/AirPortionStore';
import { useCityStore } from '@/store/CityStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import TripType from '../TripType.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

const props = defineProps<{
  cruiseFlight: any;
  isShowError?: boolean;
  minDateRangeValue: Date;
  maxDateRangeValue: Date;
  isFlight: boolean;
  validate: any;
}>();

const emits = defineEmits<{
  'update:cruiseFlight': [value: any];
  'update:isShowError': [value: any];
  clearSelectedFlight: [];
  openFlightInformationModal: [];
  openModalValidateAdult: [];
}>();

const cruiseFlight = computed({
  get() {
    return props.cruiseFlight;
  },
  set(value) {
    emits('update:cruiseFlight', value);
  }
});

const isShowError = computed({
  get() {
    return props.isShowError;
  },
  set(value) {
    emits('update:isShowError', value);
  }
});

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const { formatAirport } = useTabComposable();
const { handleOpenErrorMessage } = useModalComposable();
const currentLanguage = getCurrentLocale();
const cityStore = useCityStore();
const adminSetupStore = useAdminSetupStore();
const airPortionStore = useAirPortionStore();
const submitQuoteStore = useSubmitQuoteStore();
const viewQuoteStore = useViewQuoteStore();
const filterGate = ref();
const filterDes = ref();
const filterReturningFrom = ref();
const filterReturningTo = ref();
const isFlightRetDate = ref(false);
const diffDaysFlight: any = ref(0);
const payload: any = ref([]);
const listGateDest = computed(() => cityStore.returning);
const cruiseMinimumConfig = computed(() => adminSetupStore.minimumConfig?.cruiseConfig);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);

const gateAirs = computed(() => {
  return formatAirport(
    listGateDest.value.filter((returning: any) => returning.gatewayStatus === STATUS_OPEN && returning.gatewayApplicability.isAir),
    currentLanguage
  );
});

const desAirs = computed(() => {
  return formatAirport(
    listGateDest.value.filter((returning: any) => returning.destinationStatus === STATUS_OPEN && returning.destinationApplicability.isAir),
    currentLanguage
  );
});

const destReturningFrom = computed(() => {
  return formatAirport(
    listGateDest.value.filter((returning: any) => returning.destinationStatus === STATUS_OPEN && returning.destinationApplicability.isAir),
    currentLanguage
  );
});

const destReturningTo = computed(() => {
  return formatAirport(
    listGateDest.value.filter((returning: any) => returning.gatewayStatus === STATUS_OPEN && returning.gatewayApplicability.isAir),
    currentLanguage
  );
});

const disabledSelectFlight = computed(() => {
  return !!Object.values(props.validate).find((item: any) => item.length);
});

const changeTypeTrip = () => {
  cruiseFlight.value.gateway = '';
  cruiseFlight.value.destination = '';
  cruiseFlight.value.returningFrom = '';
  cruiseFlight.value.returningTo = '';
  cruiseFlight.value.dateRange = [];
  cruiseFlight.value.depDate = null;
  cruiseFlight.value.duration = '0';
};

const searchGateway = (event: any) => {
  const gateCruiseListSorted = gateAirs.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query.trim().length) {
    filterGate.value = [...gateCruiseListSorted];
  } else {
    filterGate.value = gateCruiseListSorted.filter((air: any) => {
      return (
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchDestinationAir = (event: any) => {
  const destCruiseListSorted = desAirs.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query.trim().length) {
    filterDes.value = [...destCruiseListSorted.slice(0, 100)];
  } else {
    filterDes.value = destCruiseListSorted.filter((air: any) => {
      return (
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchCruiseReturningFrom = (event: any) => {
  const returnFromListSorted = destReturningFrom.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query.trim().length) {
    filterReturningFrom.value = [...returnFromListSorted.slice(0, 100)];
  } else {
    filterReturningFrom.value = returnFromListSorted.filter((air: any) => {
      return (
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchCruiseReturningTo = (event: any) => {
  const returnToListSorted = destReturningTo.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query.trim().length) {
    filterReturningTo.value = [...returnToListSorted.slice(0, 100)];
  } else {
    filterReturningTo.value = returnToListSorted.filter((air: any) => {
      return (
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const changeDate = (cruiseFlightData: any) => {
  if (!cruiseFlightData.dateRange[1]) {
    isFlightRetDate.value = true;
  } else {
    isFlightRetDate.value = false;
  }

  diffDaysFlight.value = DateTime.fromJSDate(new Date(cruiseFlightData.dateRange[1]))
    .diff(DateTime.fromJSDate(new Date(cruiseFlightData.dateRange[0])), 'days')
    .toObject().days;
  diffDaysFlight.value = Math.ceil(diffDaysFlight.value);
  if (diffDaysFlight.value < 0) {
    cruiseFlightData.duration = '0';
    return;
  }
  if (isNaN(diffDaysFlight.value) || diffDaysFlight.value === 0) {
    cruiseFlightData.duration = '1';
  } else {
    cruiseFlightData.duration = diffDaysFlight.value.toString();
  }
};

const updateIsEconomy = (value: boolean) => {
  if (!value) {
    cruiseFlight.value.adults = 0;
    cruiseFlight.value.children = 0;
  }
};

const updateIsBusiness = (value: boolean) => {
  if (!value) {
    cruiseFlight.value.businessAdults = 0;
    cruiseFlight.value.businessChildren = 0;
  }
};

const validateFormCruiseAdvanced = () => {
  let isValidateAdvanced = true;
  if (cruiseFlight.value.tripTypeSelected === ROUNDTRIP && (!cruiseFlight.value.dateRange[0] || !cruiseFlight.value.dateRange[1])) {
    isValidateAdvanced = false;
  }
  if (cruiseFlight.value.tripTypeSelected === OPENJAW && (!cruiseFlight.value.dateRange[0] || !cruiseFlight.value.dateRange[1] || !cruiseFlight.value.returningFrom || !cruiseFlight.value.returningTo)) {
    isValidateAdvanced = false;
  }
  if (cruiseFlight.value.tripTypeSelected === ONEWAY && !cruiseFlight.value.depDate) {
    isValidateAdvanced = false;
  }
  return props.isFlight ? isValidateAdvanced : !isValidateAdvanced;
};

const showUnProcessBrochureCruise = () => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      modal: true
    },
    data: {
      message: t('common.labels.process_jaw'),
      title: t('common.labels.unable_to_process'),
      isAlignCenter: true
    },
    templates: {
      header: () => {
        return [h('div')];
      },
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('common.buttons.ok'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    }
  });
};

function createFlightPayloadObject(flight: any) {
  return {
    gateCode: flight?.gateway?.airportCode,
    destCode: flight?.destination?.airportCode,
    depDate: formatDate(flight?.dateRange[0]),
    retDate: formatDate(flight?.dateRange[1]),
    direct: flight?.directFlight
  };
}

const checkPassengerAir = (flight: any) => {
  return +flight.adults + +flight.businessAdults + +flight.children + +flight.businessChildren < cruiseMinimumConfig.value.minPax;
};

const clearSelectedFlight = () => {
  emits('clearSelectedFlight');
};

const openFlightInformationModal = () => {
  emits('openFlightInformationModal');
};

const openSelectFlightDialog = async () => {
  let payloadCruiseOpenJaw: any = [];
  let payloadCruiseOneway: any = [];
  const flightPayload = createFlightPayloadObject(cruiseFlight.value);

  if (cruiseFlight.value.tripTypeSelected === ROUNDTRIP) {
    payload.value = flightPayload;
  }
  if (cruiseFlight.value.tripTypeSelected === OPENJAW) {
    payloadCruiseOpenJaw = [
      {
        gateCode: cruiseFlight.value.gateway.airportCode,
        destCode: cruiseFlight.value.destination.airportCode,
        depDate: formatDate(cruiseFlight.value.dateRange[0]),
        direct: cruiseFlight.value.directFlight
      },
      {
        gateCode: cruiseFlight.value.returningFrom.airportCode,
        destCode: cruiseFlight.value.returningTo.airportCode,
        depDate: formatDate(cruiseFlight.value.dateRange[1]),
        direct: cruiseFlight.value.directFlight
      }
    ];
  }
  if (cruiseFlight.value.tripTypeSelected === ONEWAY) {
    payloadCruiseOneway = {
      gateCode: cruiseFlight.value.gateway.airportCode,
      destCode: cruiseFlight.value.destination.airportCode,
      depDate: formatDate(cruiseFlight.value.depDate),
      direct: cruiseFlight.value.directFlight
    };
  }
  clearSelectedFlight();
  isShowError.value = true;
  if (!isWorksheetRequote.value && checkPassengerAir(cruiseFlight.value)) {
    emits('openModalValidateAdult');
  } else {
    await handleSearchFlight(payload.value, payloadCruiseOpenJaw, payloadCruiseOneway);
    if (!submitQuoteStore.isLoading) {
      openFlightInformationModal();
    }
  }
};

const handleSearchFlight = async (payload: any, payloadCruiseOpenJaw: any, payloadCruiseOneway: any) => {
  submitQuoteStore.isLoading = true;
  if (cruiseFlight.value.tripTypeSelected === ROUNDTRIP) {
    await airPortionStore.getFlightInformation([payload]);
  }
  if (cruiseFlight.value.tripTypeSelected === OPENJAW) {
    await airPortionStore.searchFlightOptionJaw([payloadCruiseOpenJaw]);
  }
  if (cruiseFlight.value.tripTypeSelected === ONEWAY) {
    await airPortionStore.searchFlightOptionOneWay([payloadCruiseOneway]);
  }
  submitQuoteStore.isLoading = false;
};

const handleCheckJawInCruise = () => {
  const dataNotRoundTrip = cruiseFlight.value.tripTypeSelected !== ROUNDTRIP;
  const dataOpenJaw = cruiseFlight.value.tripTypeSelected === OPENJAW;
  let payload: any = [];
  payload = [cruiseFlight.value.gateway, cruiseFlight.value.destination];
  if (dataOpenJaw) {
    payload = [...payload, cruiseFlight.value.returningTo, cruiseFlight.value.returningFrom];
  }
  if (validateFormCruiseAdvanced()) {
    if (dataNotRoundTrip) {
      cityStore.checkJawBrochure(CRUISE, payload).then((res: any) => {
        if (!res) {
          showUnProcessBrochureCruise();
        } else {
          openSelectFlightDialog();
        }
      });
    } else {
      openSelectFlightDialog();
    }
  } else {
    isShowError.value = true;
    handleOpenErrorMessage(t('notification.fill_required'));
  }
};
</script>
<template>
  <div class="cruise-flight-form">
    <div class="flex flex-wrap gap-4 mb-4">
      <TripType v-model:referenceType="cruiseFlight.tripTypeSelected" @changeTypeTrip="changeTypeTrip()" />
    </div>
    <div class="grid">
      <div class="col-6">
        <Fluid>
          <div class="field gateway-list">
            <label for="gateway">{{ $t('advance_quote.form.gateway') }}</label>
            <AutoComplete
              v-model="cruiseFlight.gateway"
              :suggestions="filterGate"
              @complete="searchGateway($event)"
              completeOnFocus
              :optionLabel="getTextVariant(currentLanguage, 'airportNameEn', 'airportNameFr')"
              :class="isShowError && !cruiseFlight.gateway ? 'p-invalid' : ''"
              class="w-full"
              forceSelection
              id="gateway"
            >
              <template #option="slotProps">
                <div class="flex align-options-center">
                  <div>
                    <div>{{ getTextVariant(currentLanguage, slotProps.option.airportNameEn, slotProps.option.airportNameFr) }} ({{ slotProps.option.airportCode }})</div>
                    <div class="text-xs">
                      {{ getTextVariant(currentLanguage, slotProps.option.cityNameEn, slotProps.option.cityNameFr) }}, {{ (slotProps.option.province ?? currentLanguage === LANG_ENGLISH) ? slotProps.option.regionEn : slotProps.option.regionFr }},
                      {{ getTextVariant(currentLanguage, slotProps.option.countryNameEn, slotProps.option.countryNameFr) }}
                    </div>
                  </div>
                </div>
              </template>
            </AutoComplete>
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.gateway"> {{ $t('validate.required') }} </span>
          </div>
        </Fluid>
      </div>

      <div class="col-6">
        <Fluid>
          <div class="field destination-list">
            <label for="destination">{{ $t('advance_quote.form.destination') }}</label>
            <AutoComplete
              v-model="cruiseFlight.destination"
              :suggestions="filterDes"
              @complete="searchDestinationAir($event)"
              completeOnFocus
              :optionLabel="getTextVariant(currentLanguage, 'airportNameEn', 'airportNameFr')"
              :class="isShowError && !cruiseFlight.destination ? 'p-invalid' : ''"
              class="w-full"
              forceSelection
              id="destination"
            >
              <template #option="slotProps">
                <div class="flex align-options-center">
                  <div>
                    <div>{{ getTextVariant(currentLanguage, slotProps.option.airportNameEn, slotProps.option.airportNameFr) }} ({{ slotProps.option.airportCode }})</div>
                    <div class="text-xs">
                      {{ getTextVariant(currentLanguage, slotProps.option.cityNameEn, slotProps.option.cityNameFr) }}, {{ (slotProps.option.province ?? currentLanguage === LANG_ENGLISH) ? slotProps.option.regionEn : slotProps.option.regionFr }},
                      {{ getTextVariant(currentLanguage, slotProps.option.countryNameEn, slotProps.option.countryNameFr) }}
                    </div>
                  </div>
                </div>
              </template>
            </AutoComplete>
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.destination"> {{ $t('validate.required') }} </span>
          </div>
        </Fluid>
      </div>
    </div>

    <div v-if="cruiseFlight.tripTypeSelected === OPENJAW" class="grid">
      <div class="col-12 sm:col-6">
        <Fluid>
          <div class="field returning-from-list">
            <label for="returning-from">{{ $t('advance_quote.form.returning_from') }}</label>
            <div class="flex align-items-center">
              <AutoComplete
                v-model="cruiseFlight.returningFrom"
                :suggestions="filterReturningFrom"
                @complete="searchCruiseReturningFrom($event)"
                completeOnFocus
                :optionLabel="getTextVariant(currentLanguage, 'airportNameEn', 'airportNameFr')"
                :class="isShowError && !cruiseFlight.returningFrom ? 'p-invalid' : ''"
                class="w-full"
                forceSelection
                id="returning-from"
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ getTextVariant(currentLanguage, slotProps.option.airportNameEn, slotProps.option.airportNameFr) }} ({{ slotProps.option.airportCode }})</div>
                      <div class="text-xs">
                        {{ getTextVariant(currentLanguage, slotProps.option.cityNameEn, slotProps.option.cityNameFr) }}, {{ (slotProps.option.province ?? currentLanguage == 'en') ? slotProps.option.regionEn : slotProps.option.regionFr }},
                        {{ getTextVariant(currentLanguage, slotProps.option.countryNameEn, slotProps.option.countryNameFr) }}
                      </div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
            </div>
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.returningFrom"> {{ $t('validate.required') }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 sm:col-6">
        <Fluid>
          <div class="field returning-to-list">
            <label for="returning-to">{{ $t('advance_quote.form.returning_to') }}</label>
            <div class="flex align-items-center">
              <AutoComplete
                v-model="cruiseFlight.returningTo"
                :suggestions="filterReturningTo"
                @complete="searchCruiseReturningTo($event)"
                completeOnFocus
                :optionLabel="getTextVariant(currentLanguage, 'airportNameEn', 'airportNameFr')"
                :class="isShowError && !cruiseFlight.returningTo ? 'p-invalid' : ''"
                class="w-full"
                forceSelection
                id="returning-to"
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ getTextVariant(currentLanguage, slotProps.option.airportNameEn, slotProps.option.airportNameFr) }} ({{ slotProps.option.airportCode }})</div>
                      <div class="text-xs">
                        {{ getTextVariant(currentLanguage, slotProps.option.cityNameEn, slotProps.option.cityNameFr) }}, {{ (slotProps.option.province ?? currentLanguage == 'en') ? slotProps.option.regionEn : slotProps.option.regionFr }},
                        {{ getTextVariant(currentLanguage, slotProps.option.countryNameEn, slotProps.option.countryNameFr) }}
                      </div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
            </div>
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.returningTo"> {{ $t('validate.required') }} </span>
          </div>
        </Fluid>
      </div>
    </div>

    <div class="grid">
      <div class="col-6">
        <Fluid>
          <div v-if="cruiseFlight.tripTypeSelected === ONEWAY" class="field departure-date">
            <label for="dept-date">{{ $t('create_quote.departure_date') }}</label>
            <DatePicker
              id="dept-date"
              v-model="cruiseFlight.depDate"
              :minDate="minDateRangeValue"
              :maxDate="maxDateRangeValue"
              dateFormat="dd-M-yy"
              :class="isShowError && !cruiseFlight.depDate ? 'p-invalid' : ''"
              :selectOtherMonths="true"
              :placeholder="$t('common.date_format')"
            />
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.depDate"> {{ $t('validate.required') }} </span>
          </div>
          <div v-else class="field date-range">
            <label for="calendar-return-date">{{ $t('advance_quote.form.date_range') }}</label>
            <DatePicker
              v-model="cruiseFlight.dateRange"
              selectionMode="range"
              :minDate="minDateRangeValue"
              :maxDate="maxDateRangeValue"
              dateFormat="dd-M-yy"
              :selectOtherMonths="true"
              :class="isShowError && !cruiseFlight.dateRange?.length ? 'p-invalid' : ''"
              @update:modelValue="changeDate(cruiseFlight)"
              :placeholder="$t('common.date_format_range')"
              id="calendar-return-date"
            />
            <span class="text-red-500" v-if="isShowError && !cruiseFlight.dateRange?.length"> {{ $t('validate.required') }} </span>
            <br />
            <span class="text-red-500" v-if="isFlightRetDate">{{ $t('create_quote.required_return_date') }}</span>
          </div>
        </Fluid>
      </div>

      <div class="col-6">
        <Fluid>
          <div class="field">
            <label for="duration" v-tooltip.top="$t('advance_quote.form.duration')">{{ $t('advance_quote.form.duration') }}</label>
            <InputText id="duration" v-model="cruiseFlight.duration" :disabled="true" :placeholder="$t('advance_quote.form.duration')" />
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid cruise-class-number">
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <div>
              <Checkbox inputId="economy" v-model="cruiseFlight.isEconomy" :binary="true" @update:modelValue="updateIsEconomy($event)" />
              <label class="ml-1" for="economy">{{ $t('worksheet.economy_class_checkbox') }}</label>
            </div>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <div>
              <Checkbox inputId="business" v-model="cruiseFlight.isBusiness" :binary="true" @update:modelValue="updateIsBusiness($event)" />
              <label class="ml-1" for="business">{{ $t('worksheet.business_checkbox') }}</label>
            </div>
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid cruise-class-number">
      <div class="col-12 md:col-3">
        <Fluid v-if="cruiseFlight.isEconomy">
          <div class="field">
            <label for="adtEco">{{ $t('advance_quote.form.adult') }}</label>
            <InputNumber :min="0" inputId="adtEco" type="text" v-model="cruiseFlight.adults" />
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid v-if="cruiseFlight.isEconomy">
          <div class="field">
            <label for="chdEco">{{ $t('advance_quote.form.children') }}</label>
            <InputNumber :min="0" inputId="chdEco" type="text" v-model="cruiseFlight.children" />
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid v-if="cruiseFlight.isBusiness">
          <div class="field">
            <label for="adtBusiness">{{ $t('advance_quote.form.adult') }}</label>
            <InputNumber :min="0" inputId="adtBusiness" type="text" v-model="cruiseFlight.businessAdults" />
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid v-if="cruiseFlight.isBusiness">
          <div class="field">
            <label for="chdBusiness">{{ $t('advance_quote.form.children') }}</label>
            <InputNumber :min="0" inputId="chdBusiness" type="text" v-model="cruiseFlight.businessChildren" />
          </div>
        </Fluid>
      </div>
    </div>
    <Fluid>
      <div class="field cruise_option">
        <label for="directFlight">{{ $t('create_quote.direct_flight') }}</label>
        <div>
          <Checkbox v-model="cruiseFlight.directFlight" inputId="isFlight" :binary="true" />
          <label class="ml-1" for="isFlight">{{ $t('common.yes') }}</label>
        </div>
        <Button id="select-flight-btn" :disabled="disabledSelectFlight" :label="$t('create_quote.select_fligts')" class="uppercase select_flight_btn mt-4 bg-primary border-noround w-3" @click="handleCheckJawInCruise()"></Button>
      </div>
    </Fluid>
  </div>
</template>
